export const SALON_SPEAK_TOOLTIPS = {
  tile:
    "Practice spoken salon English with guided lessons, roleplay, vocabulary, and quick phrase playback.",
  home: {
    streak: "Your streak increases when you complete at least one attempt on consecutive salon-local dates.",
    dailyGoal: "Daily goal tracks completed lesson attempts in the salon timezone.",
  },
  lessons: {
    record: "Tap Record, speak clearly, and review transcript + score feedback.",
    speed: "Use Slow for pronunciation practice and Normal for natural pace.",
    translation:
      "Toggle Vietnamese translations line by line while practicing English. When visible, VN Slow/Normal uses AI Vietnamese voice when available.",
    levelUnlock:
      "Complete every lesson in the current level to unlock the next level. If content is not published yet, the level will show Coming soon.",
    markComplete:
      "Use Mark Complete to update lesson progress when you are done reviewing, even without recording.",
  },
  roleplay: {
    record: "Respond in full salon sentences. Keywords unlock the next branch.",
    grading:
      "Roleplay grading includes keyword hits, politeness words, and transcript match score.",
    salonMode:
      "Salon mode tries to improve microphone capture when available. If not, speak closer to the microphone.",
  },
  vocab: {
    favorite: "Save words for quick review in Favorites.",
    record: "Practice speaking the word and get instant keyword-match feedback.",
  },
  phrases: {
    favorite: "Save common lines to Favorites for quick daily practice.",
    offline: "Offline pack will be available in a future update.",
  },
};

export const SALON_SPEAK_HELP_SECTIONS = [
  {
    id: "workflow",
    title: "Listen, Repeat, Record, Feedback",
    description: "Use the same speaking loop for lessons, roleplay, and vocabulary.",
    anchorId: "salonspeak-workflow",
    bullets: [
      "Step 1: Tap Listen (Slow or Normal).",
      "Step 2: Repeat out loud once or twice.",
      "Step 3: Tap Record and speak clearly.",
      "Step 4: Review transcript, score, and suggestions.",
    ],
    tips: [
      "Use Slow first for difficult phrases.",
      "Keep responses professional and client-friendly.",
    ],
  },
  {
    id: "levels",
    title: "How To Unlock Levels",
    description: "SalonSpeak uses a sequential level path to guide skill progression.",
    anchorId: "salonspeak-levels",
    bullets: [
      "Finish all lessons in your current level first.",
      "When all lessons in a level are completed, the next level unlocks.",
      "If a level has no published lessons yet, it will display Coming soon.",
      "Locked means the previous level is not fully completed yet.",
    ],
    tips: [
      "Use Lesson feedback suggestions to pass each lesson faster.",
      "Check the Done status on each lesson row before moving up.",
    ],
  },
  {
    id: "translations",
    title: "Translation Toggle",
    description: "Vietnamese lines support understanding and can be played back with an AI Vietnamese voice.",
    anchorId: "salonspeak-translations",
    bullets: [
      "Turn translations on to learn meaning quickly.",
      "Use VN Slow/Normal to hear the Vietnamese translation out loud.",
      "Turn translations off when you want full-English drills.",
    ],
    tips: [
      "Try one round with translations and one round without.",
    ],
  },
  {
    id: "scoring",
    title: "How Scoring Works",
    description: "MVP score combines word match, politeness language, and attempt completion.",
    anchorId: "salonspeak-scoring",
    bullets: [
      "Transcript match contributes up to 70 points.",
      "Politeness markers contribute 15 points.",
      "Recorded attempt completion contributes 15 points.",
      "Total score is capped at 100.",
    ],
    tips: [
      "Focus on required keywords first, then smooth delivery.",
    ],
  },
  {
    id: "roleplay",
    title: "Roleplay Grading",
    description: "Roleplay evaluates branch keywords plus polite and complete responses.",
    anchorId: "salonspeak-roleplay",
    bullets: [
      "Scenario turns branch using recognized keywords.",
      "Summary shows keyword coverage and average score.",
      "Use Save to Progress after completing a scenario.",
    ],
    tips: [
      "If branching feels stuck, use short direct sentences with key terms.",
    ],
  },
  {
    id: "offline",
    title: "Offline and Browser Limits",
    description: "Speech recognition support varies by browser and device.",
    anchorId: "salonspeak-offline",
    bullets: [
      "If transcript is unavailable, attempts still save as completed practice.",
      "Use self-check prompts when the browser cannot transcribe speech.",
      "Quick Phrases includes an offline-pack placeholder for future release.",
    ],
    tips: [
      "For better recognition, reduce background noise and speak near the mic.",
    ],
  },
  {
    id: "safety",
    title: "Professional Language Safety",
    description: "Use workplace-safe language suitable for client communication.",
    anchorId: "salonspeak-safety",
    bullets: [
      "Keep tone polite and respectful.",
      "Avoid slang or unprofessional phrases.",
      "Use service-focused wording for upsell and aftercare.",
    ],
    tips: [
      "When unsure, default to simple polite English.",
    ],
  },
];

export const SALON_SPEAK_TROUBLESHOOTING = [
  {
    title: "No transcript appears",
    body: "Your browser may not support SpeechRecognition. Use the fallback recording flow and self-check prompt.",
  },
  {
    title: "Low score even after speaking",
    body: "Include the key words shown in each lesson or roleplay turn and speak at a steady pace.",
  },
  {
    title: "Audio playback unavailable",
    body: "Your browser may block SpeechSynthesis. Check browser audio permissions and try again.",
  },
];
